import { Command as CommandPrimitive } from 'cmdk';
import { type CSSProperties, type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../../lib';
import { cmdkItemValue } from '../command';
import { Spinner } from '../spinner';

type AutocompleteProps<T> = {
  /** Free-text value of the input (controlled). */
  value: string;
  onValueChange: (value: string) => void;
  /** Server-driven suggestions; the component never filters them (`shouldFilter={false}`). */
  items: T[];
  isLoading?: boolean;
  renderItem: (item: T) => ReactNode;
  /** Stable, unique key/value for a suggestion (cmdk requires a value per item). */
  itemKey: (item: T, index: number) => string;
  onSelect: (item: T) => void;
  onBlur?: () => void;
  name?: string;
  /** Names the input: cmdk overrides its `id`/`aria-labelledby`, so a sibling label can't. */
  label?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  placeholder?: string;
  disabled?: boolean;
  itemTestId?: string;
  /** Optional leading adornment, kept visually consistent with sibling inputs. */
  icon?: ReactNode;
};

const DROPDOWN_MAX_HEIGHT = 256;
const VIEWPORT_MARGIN = 8;

/** Fixed-position box anchored to the input, flipped above when it would overflow below. */
function useAnchoredPosition(anchor: HTMLElement | null, isOpen: boolean): CSSProperties | null {
  const [style, setStyle] = useState<CSSProperties | null>(null);

  useLayoutEffect(() => {
    if (!isOpen || !anchor) {
      return;
    }
    const compute = () => {
      const rect = anchor.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
      const flipUp = spaceBelow < DROPDOWN_MAX_HEIGHT && rect.top > spaceBelow;
      setStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        maxHeight: Math.max(rect.top, spaceBelow) - VIEWPORT_MARGIN,
        ...(flipUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      });
    };
    compute();
    // Follow the input on scroll/resize; `true` catches scrolls in any ancestor.
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [anchor, isOpen]);

  return style;
}

/**
 * Generic inline free-text typeahead built on the cmdk `Command` primitive:
 * an input that keeps whatever the user types (override-friendly) with a
 * server-driven suggestion dropdown. No data fetching, no form coupling — the
 * caller owns the query and decides what a selection does.
 */
export function Autocomplete<T>({
  value,
  onValueChange,
  items,
  isLoading,
  renderItem,
  itemKey,
  onSelect,
  onBlur,
  name,
  label,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  placeholder,
  disabled,
  itemTestId,
  icon,
}: AutocompleteProps<T>) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const showDropdown = open && (isLoading || items.length > 0);
  // Portaled to <body> and positioned with `fixed` so the dropdown escapes the
  // funnel card's `overflow` (a plain absolute child would be clipped) and never
  // requires scrolling the card to read the rest of the list.
  const dropdownStyle = useAnchoredPosition(rootRef.current, showDropdown);

  return (
    // shouldFilter={false}: results come from the server, cmdk must not re-filter
    // them by the free-text value (which is the whole query, not an item token).
    <CommandPrimitive ref={rootRef} label={label} shouldFilter={false} className="relative">
      {icon && (
        <div className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground">
          {icon}
        </div>
      )}
      <CommandPrimitive.Input
        name={name}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        className={cn(
          'flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-base md:text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:border-primary focus:rounded-full disabled:cursor-not-allowed disabled:opacity-95',
          icon && 'pl-9',
        )}
        onValueChange={next => {
          onValueChange(next);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        // Closing on blur fixes the dropdown lingering after the field loses focus.
        // Items keep focus via onMouseDown preventDefault, so a click still reaches
        // onSelect before this fires.
        onBlur={() => {
          setOpen(false);
          onBlur?.();
        }}
      />
      {showDropdown &&
        createPortal(
          <div
            style={dropdownStyle ?? { position: 'fixed', visibility: 'hidden' }}
            className="z-50 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
          >
            {isLoading && items.length === 0 ? (
              <div className="flex items-center justify-center p-3">
                <Spinner />
              </div>
            ) : (
              <CommandPrimitive.List className="max-h-64 overflow-auto py-1">
                {items.map((item, index) => (
                  <CommandPrimitive.Item
                    key={itemKey(item, index)}
                    value={cmdkItemValue(itemKey(item, index))}
                    data-testid={itemTestId}
                    className={cn(
                      'flex w-full cursor-pointer items-start px-3 py-2 text-left text-sm',
                      'aria-selected:bg-accent hover:bg-accent',
                    )}
                    onMouseDown={e => e.preventDefault()}
                    onSelect={() => {
                      setOpen(false);
                      onSelect(item);
                    }}
                  >
                    {renderItem(item)}
                  </CommandPrimitive.Item>
                ))}
              </CommandPrimitive.List>
            )}
          </div>,
          document.body,
        )}
    </CommandPrimitive>
  );
}
